import axios, {
	AxiosRequestConfig,
	AxiosInstance,
	AxiosError,
	InternalAxiosRequestConfig,
	AxiosResponse,
} from 'axios';
import {
	createDPoPRequestInterceptor,
	createDPoPResponseInterceptor,
} from '@phonepe/axios-dpop-interceptor';

import { IDPoPApiClientConfig } from '../types';

export class ApiDpopClient {
	private readonly axiosInstance: AxiosInstance;

	constructor(config: IDPoPApiClientConfig) {
		this.axiosInstance = axios.create(config);
		if (!config.hasCustomInterceptor) {
			this.setupDPoPInterceptor(config.refreshStatusCodes, config.refreshToken);
		}
	}

	setupDPoPInterceptor = (
		statusCodes: Array<number> = [],
		refreshToken: (instance: AxiosInstance) => Promise<void> = () =>
			Promise.resolve(),
	) => {
		createDPoPRequestInterceptor(this.axiosInstance);
		createDPoPResponseInterceptor(
			this.axiosInstance,
			() => refreshToken?.(this.axiosInstance),
			{
				statusCodes: [...(statusCodes || [])],
			},
		);
	};

	getAxiosInstance(): AxiosInstance {
		return this.axiosInstance;
	}

	static readonly getCancelController = () => {
		const abortController = new AbortController();
		return {
			signal: abortController.signal,
			cancel: () => abortController.abort(),
		};
	};

	addRequestInterceptors(
		resolveInterceptor: (
			config: InternalAxiosRequestConfig,
		) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>,
		rejectInterceptor?: (error: AxiosError) => Promise<AxiosError>,
	) {
		this.axiosInstance.interceptors.request.use(
			resolveInterceptor,
			rejectInterceptor,
		);
	}

	addResponseInterceptors(
		resolveInterceptor:
			| ((
					value: AxiosResponse<unknown, unknown>,
			  ) =>
					| AxiosResponse<unknown, unknown>
					| Promise<AxiosResponse<unknown, unknown>>)
			| undefined,
		rejectInterceptor: ((error: unknown) => unknown) | undefined,
	) {
		this.axiosInstance.interceptors.response.use(
			resolveInterceptor,
			rejectInterceptor,
		);
	}

	async _makeRequest(config: AxiosRequestConfig) {
		return this.axiosInstance
			.request(config)
			.then((response) => response.data)
			.catch((error) => Promise.reject(Error(error)));
	}

	async get(url: string, options: AxiosRequestConfig = {}) {
		const requestConfig: AxiosRequestConfig = {
			method: 'GET',
			url,
			...options,
		};
		return await this._makeRequest(requestConfig);
	}

	async post(url: string, options: AxiosRequestConfig = {}) {
		const requestConfig: AxiosRequestConfig = {
			method: 'POST',
			url,
			...options,
		};
		return await this._makeRequest(requestConfig);
	}

	async delete(url: string, options: AxiosRequestConfig = {}) {
		const requestConfig: AxiosRequestConfig = {
			method: 'DELETE',
			url,
			...options,
		};
		return await this._makeRequest(requestConfig);
	}

	async patch(url: string, options: AxiosRequestConfig = {}) {
		const requestConfig: AxiosRequestConfig = {
			method: 'PATCH',
			url,
			...options,
		};
		return await this._makeRequest(requestConfig);
	}

	async put(url: string, options: AxiosRequestConfig = {}) {
		const requestConfig: AxiosRequestConfig = {
			method: 'PUT',
			url,
			...options,
		};
		return await this._makeRequest(requestConfig);
	}
}
